import React, { useEffect, useRef } from "react";
import { FieldErrors, FieldValues, Path, UseFormSetValue } from "react-hook-form";
import { Credential } from "../../graphql/types";
import { TextFieldStyled } from "./KeyEditorCommon";

interface IControlledTextField<T extends FieldValues> {
  errors?: FieldErrors<T>;
  required: boolean;
  fieldName: Path<T>;
  label: string;
  mode?: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  editingField?: string;
  setEditingField?: (e: string) => void;
  setValue?: UseFormSetValue<
    | T
    | {
        key_name: string;
      }
  >;
  credential?: Credential;
  isTextArea?: boolean;
  loading?: boolean;
  textAreaHeight?: string;
  placeholder?: string;
  testid?: string;
}

const ControlledTextField = <T extends FieldValues>({
  errors,
  required,
  fieldName,
  mode,
  type,
  value,
  onChange,
  editingField,
  setEditingField,
  setValue,
  credential,
  isTextArea,
  textAreaHeight,
  loading,
  placeholder,
  testid,
}: IControlledTextField<T>) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingField === fieldName && inputRef.current && !loading) {
      inputRef.current.focus();
    }
  }, [inputRef, editingField, fieldName, loading]);

  const onBlur = () => {
    if (!setValue || !setEditingField || !credential || loading) return;
    if (fieldName === "key_name") {
      setValue(fieldName, credential["name"] as Path<T>);
    } else {
      setValue(fieldName, credential.properties[fieldName]);
    }
    setEditingField("");
  };

  const editHandlers =
    editingField === fieldName
      ? { onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.currentTarget.select(), onBlur }
      : {};

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "number" && !["Backspace", "Enter", "Escape"].includes(e.key) && !/\d/.test(e.key)) {
      // Ensure numbers are introduced when the input type is number
      // In this case, only integers are allowed as this validation is associated with ports
      e.preventDefault();
      return;
    }
  };
  if (isTextArea) {
    return (
      <TextFieldStyled
        data-testid={testid}
        minRows={3}
        multiline
        maxRows={4}
        hiddenLabel
        value={value}
        onChange={onChange}
        error={errors && errors[fieldName] ? true : false}
        required={required}
        id={fieldName}
        type={type}
        placeholder={value ? undefined : placeholder}
        slotProps={{ inputLabel: { shrink: false }, htmlInput: { style: { height: textAreaHeight } } }}
        disabled={(mode === "view" && editingField !== fieldName) || loading}
        data-cy="text-field"
        onKeyDown={handleKeyDown}
        helperText={errors && errors[fieldName]?.message?.toString()}
        inputRef={inputRef}
        {...editHandlers}
      />
    );
  }
  return (
    <TextFieldStyled
      data-testid={testid}
      value={value}
      onChange={onChange}
      error={errors && errors[fieldName] ? true : false}
      required={required}
      id={fieldName}
      slotProps={{ inputLabel: { shrink: false } }}
      placeholder={value ? undefined : placeholder}
      type={type}
      disabled={(mode === "view" && editingField !== fieldName) || loading}
      data-cy="text-field"
      onKeyDown={handleKeyDown}
      helperText={errors && errors[fieldName]?.message?.toString()}
      inputRef={inputRef}
      {...editHandlers}
    />
  );
};

export { ControlledTextField, IControlledTextField };
